/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Building2, User, MailCheck, ArrowLeft, CheckCircle } from 'lucide-react';
import { authAPI } from '../api/auth';
import { useViewport } from '../hooks/useViewport';
import { useImpostazioni } from '../hooks/useImpostazioni';
import {
  emailValida, telefonoValido, capValido, partitaIvaValida,
  codiceFiscaleValido, codiceUnivocoValido, ibanValido, passwordValida,
} from '../utils/validazione';

const ACCENT = '#3B82F6';

// Campi condizionali per tipo cliente. uppercase = normalizza in maiuscolo mentre si scrive.
const CAMPI_PRIVATO = [
  { key: 'nome', label: 'Nome', autoComplete: 'given-name' },
  { key: 'cognome', label: 'Cognome', autoComplete: 'family-name' },
  { key: 'indirizzo', label: 'Indirizzo', autoComplete: 'street-address' },
  { key: 'citta', label: 'Città' },
  { key: 'cap', label: 'CAP', inputMode: 'numeric', maxLength: 5 },
  { key: 'codice_fiscale', label: 'Codice fiscale', uppercase: true, maxLength: 16 },
];
const CAMPI_AZIENDA = [
  { key: 'ragione_sociale', label: 'Ragione sociale' },
  { key: 'partita_iva', label: 'Partita IVA', inputMode: 'numeric', maxLength: 11 },
  { key: 'codice_univoco', label: 'Codice univoco (SDI)', uppercase: true, maxLength: 7 },
  { key: 'pec', label: 'PEC', type: 'email' },
  { key: 'sede', label: 'Sede legale' },
  { key: 'indirizzo_fatturazione', label: 'Indirizzo di fatturazione' },
  { key: 'iban', label: 'IBAN', uppercase: true, maxLength: 34 },
];

const VUOTO = {
  email: '', telefono: '', password: '', conferma_password: '',
  nome: '', cognome: '', indirizzo: '', citta: '', cap: '', codice_fiscale: '',
  ragione_sociale: '', partita_iva: '',
  codice_univoco: '', pec: '', sede: '', indirizzo_fatturazione: '', iban: '',
};

function valida(tipo, form) {
  const e = {};
  if (!emailValida(form.email)) e.email = 'Email non valida';
  if (!telefonoValido(form.telefono)) e.telefono = 'Numero non valido (8-15 cifre)';
  if (!passwordValida(form.password)) e.password = 'Minimo 8 caratteri';
  if (form.password !== form.conferma_password) e.conferma_password = 'Le password non coincidono';

  if (tipo === 'privato') {
    ['nome', 'cognome', 'indirizzo', 'citta'].forEach((k) => {
      if (!String(form[k] || '').trim()) e[k] = 'Campo obbligatorio';
    });
    if (!capValido(form.cap)) e.cap = 'CAP: 5 cifre';
    if (!codiceFiscaleValido(form.codice_fiscale)) e.codice_fiscale = 'Codice fiscale non valido (16 caratteri)';
  } else {
    ['ragione_sociale', 'sede', 'indirizzo_fatturazione'].forEach((k) => {
      if (!String(form[k] || '').trim()) e[k] = 'Campo obbligatorio';
    });
    if (!partitaIvaValida(form.partita_iva)) e.partita_iva = 'Partita IVA non valida (11 cifre)';
    if (!codiceUnivocoValido(form.codice_univoco)) e.codice_univoco = 'Codice SDI: 7 caratteri';
    if (!emailValida(form.pec)) e.pec = 'PEC non valida';
    if (!ibanValido(form.iban)) e.iban = 'IBAN italiano non valido';
  }
  return e;
}

function costruisciPayload(tipo, form) {
  const base = {
    tipo_cliente: tipo,
    email: form.email.trim(),
    telefono: form.telefono.trim(),
    password: form.password,
    consenso_privacy: true,
  };
  if (tipo === 'privato') {
    return {
      ...base,
      nome: form.nome.trim(), cognome: form.cognome.trim(),
      indirizzo: form.indirizzo.trim(), citta: form.citta.trim(),
      cap: form.cap.trim(), codice_fiscale: form.codice_fiscale.trim().toUpperCase(),
    };
  }
  return {
    ...base,
    ragione_sociale: form.ragione_sociale.trim(), partita_iva: form.partita_iva.trim(),
    codice_univoco: form.codice_univoco.trim().toUpperCase(), pec: form.pec.trim(),
    sede: form.sede.trim(), indirizzo_fatturazione: form.indirizzo_fatturazione.trim(),
    iban: form.iban.replace(/\s/g, '').toUpperCase(),
  };
}

export default function Registrazione() {
  const impostazioni = useImpostazioni();
  const { isMobile } = useViewport();
  const [tipo, setTipo] = useState('privato');
  const [form, setForm] = useState(VUOTO);
  const [errori, setErrori] = useState({});
  const [erroreGenerale, setErroreGenerale] = useState('');
  const [inviata, setInviata] = useState(false);
  const [consenso, setConsenso] = useState(false);

  const registraMut = useMutation({
    mutationFn: () => authAPI.register(costruisciPayload(tipo, form)),
    onSuccess: () => { setErroreGenerale(''); setInviata(true); },
    onError: (err) => {
      const st = err.response?.status;
      if (st === 409) setErroreGenerale('Esiste già un account con questa email.');
      else setErroreGenerale(err.response?.data?.error || err.response?.data?.message || 'Errore durante la registrazione. Riprova.');
    },
  });

  const resendMut = useMutation({ mutationFn: () => authAPI.resendVerification(form.email.trim()) });

  const setCampo = (key, opts = {}) => (e) => {
    const v = opts.uppercase ? e.target.value.toUpperCase() : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
    setErrori((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const submit = (e) => {
    e.preventDefault();
    setErroreGenerale('');
    const eMap = valida(tipo, form);
    setErrori(eMap);
    if (Object.keys(eMap).length > 0) {
      setErroreGenerale('Controlla i campi evidenziati.');
      return;
    }
    if (!consenso) {
      setErroreGenerale('Devi accettare l\'Informativa Privacy per registrarti.');
      return;
    }
    registraMut.mutate();
  };

  // --- Schermata di conferma: email di verifica inviata ---
  if (inviata) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.card, ...(isMobile ? styles.cardMobile : {}), textAlign: 'center' }}>
          <div style={styles.iconCircle}><MailCheck size={30} color={ACCENT} /></div>
          <h1 style={styles.title}>Controlla la tua email</h1>
          <p style={styles.subtitle}>
            Abbiamo inviato un link di verifica a <strong>{form.email.trim()}</strong>.
            Clicca il link per attivare il tuo account, poi accedi.
          </p>
          <button
            onClick={() => resendMut.mutate()}
            disabled={resendMut.isPending || resendMut.isSuccess}
            style={{ ...styles.secondaryBtn, opacity: resendMut.isPending ? 0.6 : 1 }}
          >
            {resendMut.isSuccess ? <><CheckCircle size={16} /> Email reinviata</> : (resendMut.isPending ? 'Invio...' : 'Non l\'hai ricevuta? Reinvia')}
          </button>
          <Link to="/login" style={styles.backLink}>Vai al login</Link>
        </div>
      </div>
    );
  }

  const campi = tipo === 'privato' ? CAMPI_PRIVATO : CAMPI_AZIENDA;

  return (
    <div style={styles.container}>
      <div style={{ ...styles.card, ...(isMobile ? styles.cardMobile : {}) }}>
        <h1 style={styles.title}>Crea il tuo account</h1>
        <p style={styles.subtitle}>{impostazioni.app_nome} · Area Cliente</p>

        {/* Selettore tipo cliente */}
        <div style={styles.segmented}>
          {[{ v: 'privato', label: 'Privato', icon: User }, { v: 'azienda', label: 'Azienda', icon: Building2 }].map(({ v, label, icon: Icon }) => (
            <button
              key={v}
              type="button"
              onClick={() => { setTipo(v); setErrori({}); setErroreGenerale(''); }}
              style={{ ...styles.segBtn, ...(tipo === v ? styles.segBtnActive : {}) }}
            >
              <Icon size={17} /> {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} style={styles.form} noValidate>
          {/* Sezione comune */}
          <Campo label="Email" type="email" value={form.email} onChange={setCampo('email')} error={errori.email} autoComplete="email" />
          <Campo label="Telefono" type="tel" value={form.telefono} onChange={setCampo('telefono')} error={errori.telefono} autoComplete="tel" placeholder="es. +39 333 1234567" />
          <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
            <Campo label="Password" type="password" value={form.password} onChange={setCampo('password')} error={errori.password} autoComplete="new-password" />
            <Campo label="Conferma password" type="password" value={form.conferma_password} onChange={setCampo('conferma_password')} error={errori.conferma_password} autoComplete="new-password" />
          </div>

          <div style={styles.divider} />

          {/* Sezione condizionale */}
          {campi.map((c) => (
            <Campo
              key={c.key}
              label={c.label}
              type={c.type || 'text'}
              value={form[c.key]}
              onChange={setCampo(c.key, { uppercase: c.uppercase })}
              error={errori[c.key]}
              inputMode={c.inputMode}
              maxLength={c.maxLength}
              autoComplete={c.autoComplete}
              style={c.uppercase ? { textTransform: 'uppercase' } : undefined}
            />
          ))}

          <label style={styles.consensoRow}>
            <input
              type="checkbox"
              checked={consenso}
              onChange={(e) => { setConsenso(e.target.checked); setErroreGenerale(''); }}
              style={styles.consensoCheckbox}
            />
            <span>
              Ho letto e accetto l'<Link to="/privacy" target="_blank" rel="noopener noreferrer" style={styles.consensoLink}>Informativa Privacy</Link> sul trattamento dei miei dati personali. *
            </span>
          </label>

          {erroreGenerale && <div style={styles.error}>{erroreGenerale}</div>}

          <button
            type="submit"
            disabled={registraMut.isPending || !consenso}
            style={{ ...styles.button, opacity: (registraMut.isPending || !consenso) ? 0.6 : 1, cursor: (registraMut.isPending || !consenso) ? 'not-allowed' : 'pointer' }}
          >
            {registraMut.isPending ? 'Creazione account...' : 'Registrati'}
          </button>
        </form>

        <div style={styles.footer}>
          <Link to="/login" style={styles.backLink}><ArrowLeft size={14} /> Hai già un account? Accedi</Link>
        </div>
      </div>
    </div>
  );
}

function Campo({ label, error, style, ...rest }) {
  return (
    <label style={styles.field}>
      <span style={styles.label}>{label}</span>
      <input {...rest} style={{ ...styles.input, ...(error ? styles.inputError : {}), ...(style || {}) }} />
      {error && <span style={styles.fieldError}>{error}</span>}
    </label>
  );
}

const styles = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', backgroundColor: '#F3F4F6', padding: '2rem 1rem' },
  card: { backgroundColor: 'white', padding: '2.25rem', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '100%', maxWidth: '520px' },
  cardMobile: { padding: '1.5rem 1.1rem' },
  iconCircle: { width: '64px', height: '64px', borderRadius: '50%', backgroundColor: ACCENT + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' },
  title: { fontSize: '1.6rem', fontWeight: 700, margin: 0, textAlign: 'center', color: ACCENT },
  subtitle: { textAlign: 'center', color: '#6B7280', marginTop: '0.25rem', marginBottom: '1.5rem' },
  segmented: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' },
  segBtn: { flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.7rem', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '10px', cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, color: '#374151' },
  segBtnActive: { backgroundColor: ACCENT, borderColor: ACCENT, color: 'white' },
  form: { display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  row: { display: 'flex', gap: '0.75rem' },
  divider: { height: '1px', backgroundColor: '#E5E7EB', margin: '0.35rem 0' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.85rem', fontWeight: 500, color: '#374151', flex: 1 },
  label: { fontSize: '0.85rem', fontWeight: 600 },
  input: { padding: '0.65rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '1rem', outline: 'none', width: '100%', boxSizing: 'border-box' },
  inputError: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  fieldError: { color: '#DC2626', fontSize: '0.78rem', fontWeight: 500 },
  error: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.75rem', borderRadius: '8px', fontSize: '0.85rem' },
  consensoRow: { display: 'flex', alignItems: 'flex-start', gap: '0.6rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.4, cursor: 'pointer', marginTop: '0.25rem' },
  consensoCheckbox: { width: '18px', height: '18px', flexShrink: 0, marginTop: '2px' },
  consensoLink: { color: ACCENT, fontWeight: 600 },
  button: { padding: '0.85rem', border: 'none', borderRadius: '8px', color: 'white', fontSize: '1rem', fontWeight: 600, marginTop: '0.5rem', backgroundColor: ACCENT },
  secondaryBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.7rem 1.1rem', border: '1px solid #D1D5DB', borderRadius: '8px', backgroundColor: 'white', color: '#374151', fontWeight: 600, cursor: 'pointer', marginTop: '0.5rem' },
  footer: { marginTop: '1.25rem', textAlign: 'center' },
  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', color: ACCENT, textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600, marginTop: '1rem' },
};
